import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Combobox } from "design-system/organisms/Combobox"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useDuplicateWorkflowMutation } from "../../../hooks/useDuplicateWorkflowMutation"

const GLOBAL_OPTION_VALUE = "__global__"

type CampaignOption = {
  value: string
  label: string
  muted: boolean
}

type Props = {
  workflowId: string
  name: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  scopeCampaignId?: string
}

export function DuplicateWorkflowDialog({
  workflowId,
  name,
  open,
  onOpenChange,
  scopeCampaignId,
}: Props) {
  const { t } = useTranslation()
  const { mutate: duplicate, isPending } = useDuplicateWorkflowMutation(scopeCampaignId)

  const globalOption = useMemo<CampaignOption>(
    () => ({
      value: GLOBAL_OPTION_VALUE,
      label: t("settings.workflows.duplicate.global"),
      muted: true,
    }),
    [t],
  )

  const defaultName = t("settings.workflows.copyOf", {
    name: name?.trim() || t("settings.workflows.untitled"),
  })

  const [copyName, setCopyName] = useState(defaultName)
  const [campaign, setCampaign] = useState<CampaignOption>(globalOption)
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({ value: searchQuery })

  useEffect(() => {
    if (!open) return
    setCopyName(defaultName)
    setCampaign(globalOption)
    setSearchQuery("")
    // Reset the form only when the dialog is (re)opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const { data: campaigns, isPending: isLoadingCampaigns } = useCampaignsQuery({
    query: {
      query: debouncedSearchQuery,
      page: 1,
      total: 20,
      sortBy: { field: "name", order: "asc" },
    },
  })

  const options = useMemo<CampaignOption[]>(() => {
    const campaignOptions = (campaigns ?? []).map((c) => ({
      value: c.id,
      label: c.name ?? t("campaigns.untitled"),
      muted: false,
    }))
    return [globalOption, ...campaignOptions]
  }, [campaigns, globalOption, t])

  function onConfirm() {
    const campaignId = campaign.value === GLOBAL_OPTION_VALUE ? undefined : campaign.value
    duplicate(
      { workflowId, name: copyName, campaignId },
      {
        onSuccess: () => {
          toast.success(t("settings.workflows.duplicate.success"))
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.workflows.duplicate.title")}</DialogTitle>
          <DialogDescription>{t("settings.workflows.duplicate.description")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>{t("settings.workflows.duplicate.name")}</Label>
            <Input value={copyName} onChange={(e) => setCopyName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t("settings.workflows.duplicate.campaign")}</Label>
            <Combobox
              options={options}
              value={campaign}
              onChange={(option) => {
                if (!option) return
                setCampaign({
                  value: option.value,
                  label: typeof option.label === "string" ? option.label : "",
                  muted: !!option.muted,
                })
              }}
              placeholder={t("settings.workflows.duplicate.campaignPlaceholder")}
              emptyPlaceholder={t("settings.workflows.duplicate.campaignEmpty")}
              searchPlaceholder={t("settings.workflows.duplicate.campaignSearch")}
              disabled={isLoadingCampaigns}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              className="w-full"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={isPending}>
            {t("settings.workflows.duplicate.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
